import { memo, useEffect, useRef, useState } from 'react';

import { Text, type TextProps } from './typography';

type AnimatedTimerProps = TextProps & {
  startDate: Date;
  endDate?: Date;
};

/**
 * Timer component. Add a startDate and endDate and it will display the time timeElapsed
 * If `endDate` is not provided, it will make the `timer` run infinitely
 */
export const AnimatedTimer = memo(
  ({ startDate, endDate, ...props }: AnimatedTimerProps) => {
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [timeDisplay, setTimeDisplay] = useState('');

    useEffect(() => {
      if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
        setTimeDisplay('Invalid Start Date');
        return;
      }

      const updateTimer = () => {
        const now = new Date();
        const timeElapsed = now.getTime() - startDate.getTime();
        setTimeDisplay(formatDuration(timeElapsed));
      };

      updateTimer();

      intervalRef.current = setInterval(updateTimer, 1000);

      return () => {
        intervalRef.current && clearInterval(intervalRef.current);
      };
    }, [startDate, endDate]);

    return <Text {...props}>{timeDisplay}</Text>;
  },
  (p, n) =>
    p.startDate.getTime() === n.startDate.getTime() &&
    p.endDate?.getTime() === n.endDate?.getTime()
);

/// ------ Utiltity Libraries -------

/** Formats duration in the format: `hh:mm:ss` */
function formatDuration(durationMs: number): string {
  const durationSec = Math.floor(durationMs / 1000);
  const hours = Math.floor(durationSec / 3600);
  const minutes = String(Math.floor((durationSec % 3600) / 60)).padStart(
    2,
    '0'
  );
  const seconds = String(Math.floor(durationSec % 60)).padStart(2, '0');

  const hoursStr = hours > 0 ? `${String(hours).padStart(2, '0')}:` : '';

  return `${hoursStr}${minutes}:${seconds}`;
}
